// categoryData.ts

export interface CategoryData {
  value: string;
  label: string;
  icon: string;
  description: string;
  subCategories: string[];
}

export const CATEGORIES_WITH_SUBCATEGORIES: CategoryData[] = [
  {
    value: 'overview',
    label: 'Overview',
    icon: '📋',
    description: 'Company overview, mission, vision, and core values',
    subCategories: [
      'Company Overview',
      'Mission Statement',
      'Vision Statement',
      'Core Values',
      'Company History',
      'Executive Summary',
      'Organizational Structure',
      'Culture Deck',
      'Company Profile',
      'About Us',
      'Leadership Team',
      'Board of Directors',
      'Company Timeline',
      'Annual Report Summary',
      'Business Model',
      'Value Proposition'
    ]
  },
  {
    value: 'logo_identity',
    label: 'Logo Identity',
    icon: '🏷️',
    description: 'Brand identity, logos, and visual guidelines',
    subCategories: [
      'Primary Logo',
      'Secondary Logo',
      'Logo Variations',
      'Logo Usage Guidelines',
      'Color Palette',
      'Typography',
      'Brand Guidelines',
      'Style Guide',
      'Icon Set',
      'Visual Identity',
      'Brand Book',
      'Logo History',
      'Brand Assets',
      'Logo Files',
      'Corporate Identity',
      'Visual Branding'
    ]
  },
  {
    value: 'core_functions_products',
    label: 'Core Functions & Products',
    icon: '⚡',
    description: 'Core business functions and product documentation',
    subCategories: [
      'Product Overview',
      'Product Specifications',
      'Product Roadmap',
      'Product Features',
      'Core Functions',
      'Service Offerings',
      'Product Catalog',
      'Solution Briefs',
      'Product Comparison',
      'Technical Specifications',
      'Product Architecture',
      'Feature List',
      'Product Demo',
      'Product Brochure',
      'Service Catalog',
      'Core Capabilities',
      'Product Strategy',
      'Product Lifecycle',
      'Product Documentation'
    ]
  },
  {
    value: 'sales',
    label: 'Sales',
    icon: '📊',
    description: 'Sales strategies, proposals, and client management',
    subCategories: [
      'Sales Pitch',
      'Sales Proposal',
      'Sales Deck',
      'Pricing Strategy',
      'Sales Pipeline',
      'Sales Playbook',
      'Competitive Analysis',
      'Market Positioning',
      'Client Presentations',
      'Case Studies',
      'Testimonials',
      'Lead Generation',
      'Sales Strategy',
      'Sales Training',
      'Sales Metrics',
      'Sales Enablement',
      'Client Success Stories',
      'Sales Process',
      'Sales Forecasting',
      'Go-to-Market Strategy',
      'Sales Collateral',
      'Partner Presentation',
      'Sales Scripts',
      'Objection Handling',
      'Win/Loss Analysis'
    ]
  },
  {
    value: 'marketing',
    label: 'Marketing',
    icon: '📢',
    description: 'Marketing strategies, campaigns, and brand promotion',
    subCategories: [
      'Marketing Strategy',
      'Campaign Management',
      'Content Marketing',
      'Social Media Strategy',
      'Email Marketing',
      'SEO Strategy',
      'Digital Marketing',
      'Brand Awareness',
      'Market Analysis',
      'Customer Journey',
      'Marketing Funnel',
      'Lead Nurturing',
      'Marketing Automation',
      'Event Marketing',
      'Influencer Marketing',
      'Content Calendar',
      'Marketing Analytics',
      'ROI Analysis',
      'Marketing Collateral',
      'Brand Campaigns',
      'Public Relations',
      'Marketing Plan',
      'Target Audience',
      'Customer Persona',
      'A/B Testing',
      'Marketing Channels',
      'Advertising Strategy',
      'Marketing Budget',
      'Campaign Performance',
      'Marketing Reports'
    ]
  },
  {
    value: 'hr',
    label: 'HR',
    icon: '👥',
    description: 'Human resources policies, recruitment, and employee management',
    subCategories: [
      'Recruitment Strategy',
      'Job Descriptions',
      'Interview Process',
      'Onboarding',
      'Training Material',
      'Employee Handbook',
      'Performance Reviews',
      'HR Policies',
      'Compensation',
      'Benefits Guide',
      'Employee Engagement',
      'Talent Management',
      'Recruitment Process',
      'HR Operations',
      'Employee Relations',
      'Workplace Culture',
      'Diversity & Inclusion',
      'Learning & Development',
      'HR Analytics',
      'Employee Wellness',
      'Talent Acquisition',
      'Retention Strategy',
      'Exit Interviews',
      'HR Compliance'
    ]
  },
  {
    value: 'operations_delivery',
    label: 'Operations & Delivery',
    icon: '⚙️',
    description: 'Operational processes, delivery frameworks, and quality management',
    subCategories: [
      'SOP Documentation',
      'Workflow Processes',
      'Quality Assurance',
      'Delivery Framework',
      'Project Management',
      'Resource Planning',
      'Supply Chain',
      'Inventory Management',
      'Service Delivery',
      'Operational Metrics',
      'Process Optimization',
      'Operations Strategy',
      'Delivery Excellence',
      'Operational Planning',
      'Quality Management',
      'Operational KPIs',
      'Standard Operating Procedures',
      'Business Continuity',
      'Risk Management',
      'Performance Management',
      'Process Mapping',
      'Value Stream Mapping',
      'Lean Operations',
      'Agile Delivery',
      'ITIL Processes'
    ]
  },
  {
    value: 'finance_accounting',
    label: 'Finance & Accounting',
    icon: '💰',
    description: 'Financial reports, budgeting, and accounting documents',
    subCategories: [
      'Financial Reports',
      'Budget Planning',
      'Forecasting',
      'Financial Statements',
      'Balance Sheet',
      'P&L Statements',
      'Cash Flow',
      'Audit Reports',
      'Tax Documents',
      'Compliance',
      'Billing & Invoicing',
      'Expense Reports',
      'Financial Planning',
      'Business Budget',
      'Revenue Forecast',
      'Financial Analysis',
      'Investment Reports',
      'Risk Assessment',
      'Financial Strategy',
      'Accounting Policies',
      'Financial Controls',
      'Cost Management',
      'Profitability Analysis',
      'Financial Projections',
      'Vendor Payment',
      'Receivables Management'
    ]
  },
  {
    value: 'it_systems',
    label: 'IT Systems',
    icon: '💻',
    description: 'IT infrastructure, systems architecture, and technical documentation',
    subCategories: [
      'System Architecture',
      'Technical Documentation',
      'API Documentation',
      'Security Policies',
      'IT Infrastructure',
      'Data Flow Diagrams',
      'Network Design',
      'IT Compliance',
      'Software Development',
      'System Integrations',
      'DevOps',
      'IT Strategy',
      'IT Roadmap',
      'Technical Specifications',
      'Data Architecture',
      'Cloud Strategy',
      'Cybersecurity',
      'IT Operations',
      'Disaster Recovery',
      'System Administration',
      'Database Design',
      'Application Architecture',
      'IT Audit',
      'Technology Stack',
      'Software Licenses',
      'IT Governance',
      'Data Privacy',
      'Service Level Agreements'
    ]
  },
  {
    value: 'legal',
    label: 'Legal',
    icon: '⚖️',
    description: 'Legal documents, contracts, and compliance documentation',
    subCategories: [
      'Contracts',
      'NDA Agreements',
      'Partnership Agreements',
      'Compliance Documents',
      'Legal Policies',
      'Terms of Service',
      'Privacy Policy',
      'Licensing',
      'Regulatory Documents',
      'Legal Opinions',
      'Intellectual Property',
      'Risk Management',
      'Employment Contracts',
      'Service Agreements',
      'Vendor Contracts',
      'Legal Compliance',
      'Corporate Governance',
      'Data Protection',
      'GDPR Compliance',
      'Legal Framework',
      'Dispute Resolution',
      'Contract Management',
      'Due Diligence',
      'Legal Research',
      'Regulatory Filings'
    ]
  },
  {
    value: 'research_development',
    label: 'Research & Development',
    icon: '🔬',
    description: 'R&D activities, research papers, and innovation documentation',
    subCategories: [
      'Research Reports',
      'Market Research',
      'Competitor Research',
      'User Research',
      'Product Innovation',
      'R&D Roadmap',
      'Technical Research',
      'Feasibility Studies',
      'Prototype Development',
      'Test Results',
      'Research Papers',
      'Patent Documents',
      'R&D Strategy',
      'Innovation Pipeline',
      'Technology Research',
      'Scientific Papers',
      'White Papers',
      'Research Methodology',
      'Research Findings',
      'IP Strategy',
      'Research Proposals',
      'Technical Reports',
      'Innovation Management',
      'Product Testing',
      'Quality Research'
    ]
  },
  {
    value: 'others',
    label: 'Others',
    icon: '📁',
    description: 'Miscellaneous and uncategorized documents',
    subCategories: [
      'Miscellaneous',
      'General Documents',
      'Reference Material',
      'Archive',
      'External Resources',
      'Third-party Documents',
      'Drafts',
      'Work in Progress',
      'Templates',
      'Internal Memos',
      'Project Archive',
      'Historical Documents',
      'External Reference',
      'Cross-functional Documents',
      'Shared Resources',
      'General Reference',
      'Administrative Documents'
    ]
  }
];

// Category color mapping
export const CATEGORY_COLORS: Record<string, string> = {
  'overview': '#6366F1',
  'logo_identity': '#8B5CF6',
  'core_functions_products': '#3B82F6',
  'sales': '#F59E0B',
  'marketing': '#EC4899',
  'hr': '#EC4899',
  'operations_delivery': '#14B8A6',
  'finance_accounting': '#10B981',
  'it_systems': '#06B6D4',
  'legal': '#EF4444',
  'research_development': '#8B5CF6',
  'others': '#6B7280',
};

// Category icons mapping
export const CATEGORY_ICONS: Record<string, string> = {
  'overview': '📋',
  'logo_identity': '🏷️',
  'core_functions_products': '⚡',
  'sales': '📊',
  'marketing': '📢',
  'hr': '👥',
  'operations_delivery': '⚙️',
  'finance_accounting': '💰',
  'it_systems': '💻',
  'legal': '⚖️',
  'research_development': '🔬',
  'others': '📁',
};

// Helper functions
export const getCategoryByValue = (value: string): CategoryData | undefined => {
  return CATEGORIES_WITH_SUBCATEGORIES.find(cat => cat.value === value);
};

export const getSubCategories = (categoryValue: string): string[] => {
  const category = getCategoryByValue(categoryValue);
  return category?.subCategories || [];
};

export const getCategoryLabel = (value: string): string => {
  const category = getCategoryByValue(value);
  return category?.label || value;
};

export const getCategoryColor = (value: string): string => {
  return CATEGORY_COLORS[value] || '#6B7280';
};

export const getCategoryIcon = (value: string): string => {
  return CATEGORY_ICONS[value] || '📄';
};

export const isSubCategoryValid = (categoryValue: string, subCategory: string): boolean => {
  const subCategories = getSubCategories(categoryValue);
  return subCategories.includes(subCategory);
};

// Get all category options for dropdowns
export const getCategoryOptions = () => {
  return CATEGORIES_WITH_SUBCATEGORIES.map(c => ({
    label: `${c.icon} ${c.label}`,
    value: c.value,
  }));
};

// Get subcategory options for a specific category
export const getSubCategoryOptions = (categoryValue: string) => {
  return getSubCategories(categoryValue).map(sub => ({ 
    label: sub, 
    value: sub 
  }));
};

export default CATEGORIES_WITH_SUBCATEGORIES;